import React, { useState } from 'react';
import { fetchSubmissions } from '../services/api';

const AdminPanel = () => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const response = await fetchSubmissions(password);
      if (response.success) {
        // response.data contains the parsed JSON body which has { success, data: [...] }
        const submissionsArray = response.data.data || [];
        setSubmissions(submissionsArray);
        setIsAuthenticated(true);
      } else {
        setError(response.error || 'Failed to authenticate');
      }
    } catch (err) {
      setError('An error occurred while fetching data.');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#111111] p-4 text-white admin-font">
        <div className="w-full max-w-md bg-white/5 backdrop-blur-md border border-white/10 p-8 rounded-2xl shadow-2xl">
          <h2 className="text-3xl font-bold mb-6 text-center text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">
            EB Dashboard
          </h2>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 text-white placeholder-gray-500 pr-12"
                  placeholder="Enter admin password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            {error && <p className="text-red-400 text-sm text-center">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white font-medium rounded-xl transition-all duration-200 shadow-[0_0_20px_rgba(37,99,235,0.3)] hover:shadow-[0_0_30px_rgba(37,99,235,0.5)] ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {loading ? 'Authenticating...' : 'Access Dashboard'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111111] text-white p-6 md:p-10 admin-font">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-10 border-b border-white/10 pb-6">
          <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">
            EB Dashboard ({submissions.length} Submissions)
          </h1>
          <button 
            onClick={() => setIsAuthenticated(false)}
            className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors text-sm font-medium"
          >
            Logout
          </button>
        </div>

        {submissions.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <p className="text-xl">No submissions found yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {submissions.map((sub) => (
              <div 
                key={sub.id} 
                onClick={() => setSelectedSubmission(sub)}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl hover:border-white/20 transition-all duration-300 group cursor-pointer"
              >
                
                {/* Image Section */}
                <div className="h-48 w-full bg-black/50 overflow-hidden relative">
                  {sub.photo ? (
                    <img 
                      src={sub.photo} 
                      alt={`Profile of ${sub.name}`} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-600">No Photo</div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#111111] to-transparent opacity-80"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-xl font-bold truncate">{sub.name}</h3>
                    <p className="text-blue-400 text-sm font-medium">{sub.rollno}</p>
                  </div>
                </div>

                {/* Details Section */}
                <div className="p-6 space-y-5">
                  <div className="grid grid-cols-2 gap-y-3 text-base">
                    <div>
                      <span className="text-gray-400 block text-sm uppercase tracking-wider mb-1">Branch</span>
                      <span className="font-medium text-gray-100">{sub.branch}</span>
                    </div>
                    <div>
                      <span className="text-gray-400 block text-sm uppercase tracking-wider mb-1">CGPA</span>
                      <span className="font-medium text-purple-400">{sub.cgpa || 'N/A'}</span>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-white/10 text-base">
                    <span className="text-gray-400 block text-sm uppercase tracking-wider mb-1">Skill/Domain</span>
                    <span className="font-medium text-emerald-400">{sub.skilltype}</span>
                  </div>

                  <div className="pt-4 border-t border-white/10 grid grid-cols-2 gap-y-3 text-base">
                     <div>
                      <span className="text-gray-400 block text-sm uppercase tracking-wider mb-1">Primary Dept</span>
                      <span className="font-medium text-blue-300">{sub.primary_department}</span>
                    </div>
                    <div>
                      <span className="text-gray-400 block text-sm uppercase tracking-wider mb-1">Secondary Dept</span>
                      <span className="font-medium text-purple-300">{sub.secondary_department}</span>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-white/10 space-y-4">
                    {sub.previous_experience && (
                      <div>
                        <span className="text-gray-400 block text-sm uppercase tracking-wider mb-1">Experience</span>
                        <p className="text-base text-gray-200 line-clamp-3" title={sub.previous_experience}>{sub.previous_experience}</p>
                      </div>
                    )}
                    {sub.other_societies && (
                      <div>
                        <span className="text-gray-400 block text-sm uppercase tracking-wider mb-1">Other Societies</span>
                        <p className="text-base text-gray-200 line-clamp-3" title={sub.other_societies}>{sub.other_societies}</p>
                      </div>
                    )}
                  </div>

                  {/* Links */}
                  <div className="pt-5 flex gap-4">
                    <a 
                      href={`mailto:${sub.email}`} 
                      onClick={(e) => e.stopPropagation()}
                      className="flex-1 text-center py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors text-base font-medium"
                    >
                      Email
                    </a>
                    {(sub.github || sub.linkedin) && (
                       <a 
                         href={sub.github || sub.linkedin} 
                         onClick={(e) => e.stopPropagation()}
                         target="_blank" 
                         rel="noopener noreferrer"
                         className="flex-1 text-center py-2.5 bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 border border-blue-500/30 rounded-lg transition-colors text-base font-medium"
                       >
                         {sub.github ? 'GitHub' : 'LinkedIn'}
                       </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Portfolio Modal */}
      {selectedSubmission && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6 bg-black/80 backdrop-blur-sm"
          onClick={() => setSelectedSubmission(null)}
        >
          <div 
            className="bg-[#1a1a1a] border border-white/20 w-full max-w-4xl max-h-[90vh] rounded-3xl overflow-y-auto shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button 
              onClick={() => setSelectedSubmission(null)}
              className="absolute top-4 right-4 bg-black/50 hover:bg-white/10 p-2 rounded-full text-gray-400 hover:text-white transition-colors z-10"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="flex flex-col md:flex-row">
              {/* Left Column: Photo & Quick Info */}
              <div className="w-full md:w-1/3 bg-black/40 border-r border-white/10">
                <div className="h-64 md:h-80 w-full bg-black/60 relative">
                  {selectedSubmission.photo ? (
                    <img 
                      src={selectedSubmission.photo} 
                      alt={`Profile of ${selectedSubmission.name}`} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-600">No Photo</div>
                  )}
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black to-transparent p-6">
                    <h2 className="text-3xl font-bold text-white">{selectedSubmission.name}</h2>
                    <p className="text-xl text-blue-400 font-medium">{selectedSubmission.rollno}</p>
                  </div>
                </div>
                
                <div className="p-6 space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-gray-400 block text-sm uppercase tracking-wider mb-1">Branch</span>
                      <span className="text-lg font-medium text-gray-100">{selectedSubmission.branch}</span>
                    </div>
                    <div>
                      <span className="text-gray-400 block text-sm uppercase tracking-wider mb-1">CGPA</span>
                      <span className="text-lg font-medium text-purple-400">{selectedSubmission.cgpa || 'N/A'}</span>
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-400 block text-sm uppercase tracking-wider mb-1">Skill/Domain</span>
                    <span className="text-lg font-medium text-emerald-400">{selectedSubmission.skilltype}</span>
                  </div>

                  {/* Links */}
                  <div className="pt-4 border-t border-white/10 flex flex-col gap-3">
                    <a 
                      href={`mailto:${selectedSubmission.email}`} 
                      className="flex items-center justify-center gap-2 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-colors text-base font-medium"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" /><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" /></svg>
                      Email Applicant
                    </a>
                    {(selectedSubmission.github || selectedSubmission.linkedin) && (
                      <div className="flex gap-3">
                        {selectedSubmission.github && (
                           <a 
                             href={selectedSubmission.github} 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className="flex-1 text-center py-2 bg-gray-800 hover:bg-gray-700 text-white border border-gray-600 rounded-xl transition-colors text-sm font-medium"
                           >
                             GitHub
                           </a>
                        )}
                        {selectedSubmission.linkedin && (
                           <a 
                             href={selectedSubmission.linkedin} 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className="flex-1 text-center py-2 bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 border border-blue-500/30 rounded-xl transition-colors text-sm font-medium"
                           >
                             LinkedIn
                           </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column: Detailed Answers */}
              <div className="w-full md:w-2/3 p-6 md:p-10 space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6 border-b border-white/10">
                  <div>
                    <h3 className="text-gray-400 text-sm uppercase tracking-wider mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500"></span> Primary Department
                    </h3>
                    <p className="text-xl font-medium text-blue-300">{selectedSubmission.primary_department}</p>
                  </div>
                  <div>
                    <h3 className="text-gray-400 text-sm uppercase tracking-wider mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-purple-500"></span> Secondary Department
                    </h3>
                    <p className="text-xl font-medium text-purple-300">{selectedSubmission.secondary_department}</p>
                  </div>
                </div>

                {selectedSubmission.previous_experience && (
                  <div className="pb-6 border-b border-white/10">
                    <h3 className="text-emerald-400 font-medium text-lg mb-3">Previous Experience</h3>
                    <div className="bg-black/30 p-5 rounded-2xl border border-white/5">
                      <p className="text-gray-200 leading-relaxed whitespace-pre-wrap">{selectedSubmission.previous_experience}</p>
                    </div>
                  </div>
                )}

                {selectedSubmission.other_societies && (
                  <div className="pb-6 border-b border-white/10">
                    <h3 className="text-emerald-400 font-medium text-lg mb-3">Other Societies & Time Management</h3>
                    <div className="bg-black/30 p-5 rounded-2xl border border-white/5">
                      <p className="text-gray-200 leading-relaxed whitespace-pre-wrap">{selectedSubmission.other_societies}</p>
                    </div>
                  </div>
                )}

                {selectedSubmission.remarks && (
                  <div>
                    <h3 className="text-emerald-400 font-medium text-lg mb-3">Remarks / Bio</h3>
                    <div className="bg-black/30 p-5 rounded-2xl border border-white/5">
                      <p className="text-gray-200 leading-relaxed whitespace-pre-wrap">{selectedSubmission.remarks}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
